/**
 * The unsaved-work registry, and the auto-update safety decision it feeds.
 *
 * Why this exists: freshness.ts used to only ever OFFER a reload, never take
 * one, and the comment there said why - the person may be mid-form. That rule
 * was right about the danger and wrong about the cost. Bobbie runs the Hub as
 * an installed iOS PWA, closed and reopened it several times against a fresh
 * deploy on 2026-08-01, and never got the new build. An app that only updates
 * when you notice a pill and tap it is an app that does not update.
 *
 * So the rule is now "update automatically, but only when there is provably
 * nothing to lose". This module is the "provably" half, and it is the
 * load-bearing part of the change: everything else is plumbing, but if this
 * says "clean" while a teacher is part-way through attendance or Kim is
 * mid class-post, the reload eats their work with no warning.
 *
 * Three signals, deliberately layered:
 *
 *   1. An explicit registry. A composer calls registerUnsavedWork() while it
 *      holds something a reload would erase and clearUnsavedWork() once that
 *      is sent or discarded. This is authoritative and it is the only signal
 *      that can see state the DOM cannot: a staged File that was never
 *      uploaded, a chosen recipient list, a half-built task, a submit fetch
 *      that is mid-flight against a Netlify function.
 *
 *   2. An in-flight upload counter, incremented around every Firebase Storage
 *      write (see storageUpload.ts, which is why every lib imports uploadBytes
 *      from there rather than from firebase/storage). Reloading mid-upload
 *      loses the bytes, and worse, several call sites write the doc first and
 *      the file second, so an interrupted upload can strand a record pointing
 *      at a file that never landed.
 *
 *   3. A DOM backstop for non-empty <textarea>s, evaluated in freshness.ts
 *      where the browser globals live. There are ~38 files with a textarea in
 *      them and only the genuinely risky ones are wired to the registry by
 *      hand; the backstop means the long tail still cannot lose typed prose.
 *      It is textareas ONLY on purpose. A single-line <input> is more often a
 *      search or filter box than a draft, and counting those as unsaved work
 *      would mean the app never auto-updates for anybody who left a filter
 *      typed in - a false positive here is cheap (we fall back to the pill),
 *      but enough of them and the feature is dead on arrival.
 *
 * This file imports nothing, on purpose. tests/auto-reload.test.mjs runs it
 * directly under --experimental-strip-types, the same reason photoUpload.ts
 * is standalone rather than folded into offerings.ts.
 */

/**
 * Live dirty keys. A Set rather than a boolean because several composers can
 * be mounted at once (a class page can have an open post composer and an open
 * reply box), and each has to be able to clear only its own claim.
 *
 * Keys are made instance-unique by useUnsavedWork (it appends a React useId),
 * so two mounts of the same component cannot clear each other's entry.
 */
const dirtyKeys = new Set<string>();

let uploadsInFlight = 0;

/**
 * One in-flight Storage upload. A ticket instead of a bare counter bump
 * because of a race the review caught: callers wrap upload chains in a 30s
 * timeout (withPhotoUploadTimeout), and when the TIMEOUT wins, the losing
 * upload promise may simply never settle - a stalled fetch on a dead signal
 * settles nothing. A bare try/finally around the upload then never runs its
 * finally, the counter sticks at 1 forever, and auto-update is silently
 * disabled for the life of the tab. The ticket's `settled` flag lets the
 * timeout path release the counter at timeout-loss while making the late
 * settle of the real promise (if it ever comes) a no-op instead of a double
 * decrement.
 */
export type UploadTicket = { settled: boolean };

const openTickets = new Set<UploadTicket>();

export function registerUnsavedWork(key: string): void {
  if (key) dirtyKeys.add(key);
}

export function clearUnsavedWork(key: string): void {
  dirtyKeys.delete(key);
}

export function hasRegisteredUnsavedWork(): boolean {
  return dirtyKeys.size > 0;
}

/** Debug aid: what is currently claiming to be dirty. Surfaced on /hub-check. */
export function unsavedWorkKeys(): string[] {
  return Array.from(dirtyKeys);
}

/** Test-only reset. Never call this from app code. */
export function resetUnsavedWorkForTests(): void {
  dirtyKeys.clear();
  openTickets.clear();
  uploadsInFlight = 0;
}

/** An upload is starting. Hold the ticket; settle it exactly once. */
export function openUploadTicket(): UploadTicket {
  const ticket: UploadTicket = { settled: false };
  openTickets.add(ticket);
  uploadsInFlight += 1;
  return ticket;
}

/**
 * The upload behind this ticket finished, failed, or was given up on.
 * Idempotent by design - the settled flag is what stops the "timeout lost
 * the race, then the real promise settled anyway" sequence from decrementing
 * twice and stealing another live upload's protection.
 */
export function settleUploadTicket(ticket: UploadTicket): void {
  if (ticket.settled) return;
  ticket.settled = true;
  openTickets.delete(ticket);
  // Clamped at zero so no bug in ticket accounting can ever drive the counter
  // negative and permanently convince us an upload is impossible.
  uploadsInFlight = Math.max(0, uploadsInFlight - 1);
}

/**
 * A caller-side upload timeout fired, so one in-flight upload chain has been
 * abandoned: whoever was waiting has already been told it failed. Release the
 * OLDEST open ticket, which is the chain most plausibly being given up on.
 * With concurrent uploads this can in principle release a neighbour's ticket
 * instead (the timeout may have come from a getDownloadURL hang after the
 * upload leg finished) - that slightly weakens protection for one upload,
 * which beats the alternative of a counter stuck at 1 disabling auto-update
 * for the whole tab, and it can never double-count thanks to the flag.
 */
export function abandonOldestUpload(): void {
  const first = openTickets.values().next();
  if (!first.done) settleUploadTicket(first.value);
}

export function uploadInFlight(): boolean {
  return uploadsInFlight > 0;
}

/**
 * Shared floor for everything that can programmatically reload this tab (see
 * reloadGuard.ts for the roster): each stamps the same timestamp and the two
 * AUTOMATIC paths refuse to act inside this window, so a chunk-failure reload
 * and a freshness reload cannot take turns reloading each other's fresh page,
 * and neither can fire right on the heels of a sign-out or invite-acceptance
 * reload.
 */
export const RELOAD_COOLDOWN_MS = 60 * 1000;

export type AutoUpdateInput = {
  /** /version.json disagrees with the version this bundle was built from. */
  stale: boolean;
  /** Registry claim or a non-empty textarea somewhere in the document. */
  hasUnsavedWork: boolean;
  uploadInFlight: boolean;
  /** An open [role="dialog"] or <dialog open> is in the document. */
  modalOpen: boolean;
  /** Since anything last reloaded this tab. Infinity when nothing has. */
  msSinceLastReload: number;
  /**
   * True when we already auto-reloaded aiming at exactly this deployed
   * version and came back still running the old one. Means the reload did
   * not take (a CDN edge serving a stale index.html, an iOS PWA shell that
   * refuses to re-fetch), so trying again would loop forever.
   */
  alreadyRetriedThisVersion: boolean;
  /**
   * sessionStorage AND localStorage both survived a real write/read/remove
   * probe. Every anti-loop guard above lives in one of the two, so when
   * storage is broken (private-mode edge cases, iOS storage pressure) they
   * would all fail open AT ONCE - the adversarial review drove the real code
   * under a throwing sessionStorage and got an unbounded reload loop in five
   * iterations. The rule is therefore: an automatic reload requires working
   * guards. The pill is safe without storage; the reload is not.
   */
  guardsHealthy: boolean;
};

export type AutoUpdateDecision =
  | { action: 'wait' }
  | { action: 'prompt' }
  | { action: 'reload'; reason: 'clean' };

/**
 * The whole safety judgement, as a pure function so it can be pinned by test
 * rather than reasoned about across a pile of event listeners.
 *
 * 'wait' means stay silent and keep checking. 'prompt' means fall back to the
 * old behaviour and let <FreshnessPill /> offer a manual Reload - it is the
 * safe answer whenever anything is uncertain. 'reload' means go now.
 *
 * Reload if clean, prompt if dirty, and NOTHING overrides dirty. An earlier
 * draft had a "nightly floor" where a calendar-day change plus hours of
 * idleness beat unsaved work, on the theory that a draft sitting untouched
 * overnight is abandoned. The review killed it, and the reasoning is worth
 * keeping: there is no timer in this design, so every decision runs on a
 * RETURN GESTURE - a foreground or a navigation - which means the "abandoned
 * overnight" reload would fire at the exact moment someone came back to
 * finish the draft. Kim staging a video post at 8:40pm and reopening the app
 * at 8:15am would have watched it destroyed before first paint. Bobbie's
 * "update nightly at the min" is still satisfied without the override: the
 * common case is a clean app, which reloads silently on the first morning
 * foreground, and the dirty case shows the pill instead. Nor can dirty
 * stick around for days and stall updates forever - local component drafts
 * do not survive an app relaunch, so a genuinely abandoned draft is gone
 * (along, honestly, with its text) the next time the PWA cold-starts, and
 * that launch reloads clean.
 */
export function decideAutoUpdate(input: AutoUpdateInput): AutoUpdateDecision {
  if (!input.stale) return { action: 'wait' };

  // Broken storage means broken loop guards. Never auto-reload on trust.
  if (!input.guardsHealthy) return { action: 'prompt' };

  // Something reloaded this tab a moment ago and we are still stale. Either
  // the deploy is mid-flight across CDN edges or the reload did not stick;
  // either way another reload right now is how a loop starts.
  if (input.msSinceLastReload < RELOAD_COOLDOWN_MS) return { action: 'prompt' };

  // We already spent our one automatic attempt on this exact version.
  if (input.alreadyRetriedThisVersion) return { action: 'prompt' };

  // Bytes in flight are unrecoverable and the doc may already reference them.
  if (input.uploadInFlight) return { action: 'prompt' };

  // The only path that reloads: nothing staged, nothing typed, nothing open.
  if (!input.hasUnsavedWork && !input.modalOpen) {
    return { action: 'reload', reason: 'clean' };
  }

  return { action: 'prompt' };
}
